import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Urls } from '@/lib/navigation';
import {
  Power,
  Pencil,
  Pin,
  PinOff,
  AlertTriangle,
  MessageSquare,
  MoreHorizontal,
  Trash2,
  X,
  Loader2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { sessionToAgent, type AgentInfo, type DisplayStatus } from '@/lib/agents';
import { ModalTextInput } from '@/components/inputs/ModalTextInput';
import { DismissTextButton } from '@/components/buttons/DismissTextButton';
import { ConfirmCancelButton, ConfirmDeleteButton } from '@/components/buttons/ConfirmButtons';
import { ModalOverlay } from '@/components/ModalOverlay';
import { FullPageShell } from '@/components/FullPageShell';
import { AmberBanner } from '@/components/layout/StatusBanner';
import { ContentCard } from '@/components/layout/ContentCard';
import { useAppSession } from '@/contexts/SessionContext';
import { useAgentStatus } from '@/hooks/useAgentStatus';
import type { useAgentActions } from '@/hooks/useAgentActions';
import { usePaperThemeActive } from '@/hooks/useThemeFlag';
import { AgentsPaper } from '@/components/paper/AgentsPaper';

/* ------------------------------------------------------------------ */
/*  Agent helpers                                                      */
/* ------------------------------------------------------------------ */

const statusLabel = (status: DisplayStatus) => {
  switch (status) {
    case 'starting': return 'STARTING';
    case 'on': return 'ON';
    case 'running': return 'RUNNING';
    case 'stopping': return 'STOPPING';
    case 'off': return 'OFF';
  }
};

const StatusDot = ({ status, className }: { status: DisplayStatus; className?: string }) => {
  if (status === 'starting' || status === 'stopping' || status === 'running') {
    return <Loader2 className={cn('h-2 w-2 flex-shrink-0 animate-spin text-zinc-400', className)} />;
  }
  if (status === 'off') {
    return <span className={cn('h-2 w-2 rounded-full flex-shrink-0 bg-zinc-700', className)} />;
  }
  return (
    <span className={cn('h-2 w-2 rounded-full flex-shrink-0 bg-emerald-400', className)} />
  );
};

/* ------------------------------------------------------------------ */
/*  Rename dialog                                                      */
/* ------------------------------------------------------------------ */

import { useEffect, useRef } from 'react';

const RenameDialog = ({
  value,
  onSave,
  onClose,
}: {
  value: string;
  onSave: (name: string) => void;
  onClose: () => void;
}) => {
  const [draft, setDraft] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  const save = () => {
    const trimmed = draft.trim();
    if (trimmed && trimmed !== value) onSave(trimmed);
    onClose();
  };

  return (
    <ModalOverlay className="z-[60] bg-black/50" onBackdropClick={onClose} cardClassName="mx-4">
      <h3 className="text-[15px] font-medium text-zinc-100">Rename Agent</h3>
      <ModalTextInput
        ref={inputRef}
        value={draft}
        onChange={setDraft}
        onKeyDown={(e) => {
          if (e.key === 'Enter') save();
          if (e.key === 'Escape') onClose();
        }}
        placeholder="Agent name"
      />
      <div className="mt-5 flex justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          className="h-9 rounded-lg px-4 text-[13px] text-zinc-400 transition-colors hover:bg-white/[0.06] hover:text-zinc-200"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={save}
          disabled={!draft.trim() || draft.trim() === value}
          className="h-9 rounded-lg bg-white/[0.1] px-4 text-[13px] text-zinc-100 transition-colors hover:bg-white/[0.16] disabled:opacity-40 disabled:hover:bg-white/[0.1]"
        >
          Save
        </button>
      </div>
    </ModalOverlay>
  );
};

/* ------------------------------------------------------------------ */
/*  Agent table                                                        */
/* ------------------------------------------------------------------ */

interface AgentTableProps {
  agents: AgentInfo[];
  activeChatId: string | null;
  actions: ReturnType<typeof useAgentActions>;
  onStop: (agent: AgentInfo) => void;
  onDelete: (id: string) => void;
  onRename: (agent: AgentInfo) => void;
}

const AgentTable = (props: AgentTableProps) => (
  <ContentCard>
    <table className="w-full table-fixed">
      <thead>
        <tr className="border-b border-white/[0.06] bg-white/[0.02]">
          <th className="px-2 py-3 text-left text-[12px] font-medium text-zinc-500 desktop:px-4">
            Name
          </th>
          <th className="hidden w-36 px-2 py-3 text-left text-[12px] font-medium text-zinc-500 desktop:table-cell desktop:px-4">
            Status
          </th>
          <th className="hidden w-20 px-4 py-3 text-left text-[12px] font-medium text-zinc-500 sm:table-cell">
            Uptime
          </th>
          <th className="w-28 px-2 py-3 desktop:w-56 desktop:px-4" />
        </tr>
      </thead>
      <tbody>
        {props.agents.map((agent) => (
          <AgentRow
            key={agent.id}
            agent={agent}
            activeChatId={props.activeChatId}
            actions={props.actions}
            onStop={props.onStop}
            onDelete={props.onDelete}
            onRename={props.onRename}
          />
        ))}
      </tbody>
    </table>
  </ContentCard>
);

/* ------------------------------------------------------------------ */
/*  Agent row                                                          */
/* ------------------------------------------------------------------ */

interface AgentRowProps {
  agent: AgentInfo;
  activeChatId: string | null;
  actions: ReturnType<typeof useAgentActions>;
  onStop: (agent: AgentInfo) => void;
  onDelete: (id: string) => void;
  onRename: (agent: AgentInfo) => void;
}

const AgentRow = (props: AgentRowProps) => {
  const { agent, activeChatId, actions, onStop, onDelete, onRename } = props;
  const isSwitching = actions.switchingAgentId === agent.id;

  return (
    <tr
      onClick={() => {
        if (actions.switchingAgentId || agent.status === 'stopping' || agent.status === 'starting') return;
        actions.handleSelectAgentFromIsland(agent.id);
      }}
      className={cn(
        'group border-b border-white/[0.04] transition-colors last:border-0',
        actions.switchingAgentId || agent.status === 'stopping' || agent.status === 'starting'
          ? (isSwitching ? 'bg-white/[0.04]' : 'opacity-50 cursor-default')
          : 'cursor-pointer hover:bg-white/[0.02]',
        !actions.switchingAgentId && activeChatId === agent.id && 'bg-white/[0.03]',
      )}
    >
      <td className={cn(
        'overflow-hidden px-2 py-3 desktop:px-4',
        agent.pinned && 'border-l-2 border-l-zinc-600',
      )}>
        <div className="flex min-w-0 items-center gap-2">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              actions.handlePinAgent(agent.id, !agent.pinned);
            }}
            className={cn(
              'group/pin relative flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md transition-all hover:bg-white/[0.05]',
              agent.pinned
                ? 'text-zinc-400 hover:text-zinc-300'
                : 'text-zinc-700 hover:text-zinc-400 desktop:opacity-0 desktop:group-hover:opacity-100',
            )}
          >
            {agent.pinned ? (
              <>
                <Pin className="h-3.5 w-3.5 group-hover/pin:hidden" />
                <PinOff className="hidden h-3.5 w-3.5 group-hover/pin:block" />
              </>
            ) : (
              <Pin className="h-3.5 w-3.5" />
            )}
          </button>
          {isSwitching ? (
            <Loader2 className="h-3.5 w-3.5 flex-shrink-0 animate-spin text-zinc-400 desktop:hidden" />
          ) : (
            <StatusDot status={agent.status} className="desktop:hidden" />
          )}
          <div className="min-w-0 flex-1">
            <span className={cn(
              'truncate text-left text-[14px]',
              isSwitching ? 'text-zinc-100' : 'text-zinc-200',
            )}>
              {agent.name}
            </span>
            {isSwitching && (
              <span className="ml-2 text-[12px] text-zinc-500 desktop:hidden">Loading…</span>
            )}
          </div>
        </div>
      </td>
      <td className="hidden px-2 py-3 desktop:table-cell desktop:px-4">
        {isSwitching ? (
          <div className="flex items-center gap-2">
            <Loader2 className="h-3.5 w-3.5 animate-spin text-zinc-400" />
            <span className="text-[12px] text-zinc-400">Loading…</span>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <StatusDot status={agent.status} />
            <span className="text-[12px] text-zinc-500">
              {statusLabel(agent.status)}
            </span>
          </div>
        )}
      </td>
      <td className="hidden px-4 py-3 sm:table-cell">
        <span className="font-mono text-[12px] text-zinc-500">
          {agent.uptime ?? '—'}
        </span>
      </td>
      <td
        className="px-2 py-3 desktop:px-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className={cn(
          'flex items-center justify-end gap-2 desktop:gap-1',
          (actions.switchingAgentId || agent.status === 'stopping') && 'pointer-events-none opacity-40',
        )}>
          <button
            type="button"
            disabled={!!actions.switchingAgentId || agent.status === 'stopping' || agent.status === 'starting'}
            onClick={() => actions.handleSelectAgentFromIsland(agent.id)}
            className="flex h-7 w-7 items-center justify-center rounded-lg text-zinc-500 transition-all hover:bg-white/[0.05] hover:text-zinc-300 desktop:w-auto desktop:gap-1.5 desktop:px-2 desktop:opacity-0 desktop:group-hover:opacity-100"
          >
            <MessageSquare className="h-3.5 w-3.5" />
            <span className="hidden text-[12px] desktop:inline">Chat</span>
          </button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="flex h-7 w-7 items-center justify-center rounded-lg text-zinc-600 opacity-100 transition-all hover:bg-white/[0.05] hover:text-zinc-400 desktop:opacity-0 desktop:group-hover:opacity-100 data-[state=open]:opacity-100"
              >
                <MoreHorizontal className="h-4 w-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="w-44 rounded-xl border-white/[0.08] bg-zinc-900/95 p-1.5 backdrop-blur-xl"
            >
              {(agent.status === 'on' || agent.status === 'running') && (
                <DropdownMenuItem
                  onClick={() => onStop(agent)}
                  className="rounded-lg text-[13px] text-red-400 focus:bg-white/[0.05] focus:text-red-400"
                >
                  <Power className="mr-2 h-3.5 w-3.5" />
                  Stop
                </DropdownMenuItem>
              )}
              {agent.status === 'off' && (
                <DropdownMenuItem
                  onClick={() => actions.handleStartAgent(agent.id)}
                  className="rounded-lg text-[13px] text-emerald-400 focus:bg-white/[0.05] focus:text-emerald-400"
                >
                  <Power className="mr-2 h-3.5 w-3.5" />
                  Start
                </DropdownMenuItem>
              )}
              {agent.status !== 'stopping' && agent.status !== 'starting' && (
                <>
                  <DropdownMenuItem
                    onClick={() => onRename(agent)}
                    className="rounded-lg text-[13px] text-zinc-300 focus:bg-white/[0.05] focus:text-zinc-200"
                  >
                    <Pencil className="mr-2 h-3.5 w-3.5" />
                    Rename
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => onDelete(agent.id)}
                    className="rounded-lg text-[13px] text-red-400 focus:bg-white/[0.05] focus:text-red-400"
                  >
                    <Trash2 className="mr-2 h-3.5 w-3.5" />
                    Delete
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </td>
    </tr>
  );
};

/* ------------------------------------------------------------------ */
/*  AgentsPage — route target                                          */
/* ------------------------------------------------------------------ */

export const AgentsPage = () => {
  const isPaper = usePaperThemeActive();
  if (isPaper) return <AgentsPaper />;
  return <AgentsPageLegacy />;
};

const AgentsPageLegacy = () => {
  const navigate = useNavigate();
  const { session, actions, agentError, dismissAgentError } = useAppSession();
  const { chat, chatHistory } = session;

  const { runningCount } = useAgentStatus(
    chat.activeChatId,
    chat.activeChat?.isRunning,
    chatHistory.sessions,
  );

  const agents = useMemo(() => {
    const all = chatHistory.sessions.map(sessionToAgent);
    const pinned = all.filter((a) => a.pinned).sort((a, b) => (b.lastInteractionMs ?? 0) - (a.lastInteractionMs ?? 0));
    const unpinned = all.filter((a) => !a.pinned);
    return [...pinned, ...unpinned];
  }, [chatHistory.sessions]);

  const [renamingAgent, setRenamingAgent] = useState<AgentInfo | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const handleStop = useCallback(
    (agent: AgentInfo) => actions.handleStopAgent(agent.id),
    [actions],
  );

  const handleDelete = useCallback(
    (id: string) => setConfirmDeleteId(id),
    [],
  );

  const confirmDelete = useCallback(
    (id: string) => {
      setConfirmDeleteId(null);
      void Promise.resolve(actions.handleDeleteAgent(id)).catch(() => {});
    },
    [actions],
  );

  const goBack = useCallback(() => navigate(Urls.home()), [navigate]);

  return (
    <FullPageShell
      onClose={goBack}
      className="z-40"
      headerClassName="z-50 hidden desktop:flex"
      mainClassName="pt-6 desktop:pt-20"
    >
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-xl text-zinc-100">Agents</h1>
          <p className="mt-1 text-[13px] text-zinc-500">
            {runningCount} on &middot; {agents.length} total
          </p>
        </div>
        <button
          type="button"
          onClick={goBack}
          className="flex h-8 w-8 items-center justify-center rounded-lg text-zinc-500 transition-colors hover:bg-white/[0.06] hover:text-zinc-300 desktop:hidden"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      <div className="mt-6">
        {agentError && (
          <AmberBanner className="mb-4">
            <AlertTriangle className="h-4 w-4 flex-shrink-0 text-amber-400" />
            <p className="flex-1 text-[13px] text-amber-200">{agentError}</p>
            <DismissTextButton onClick={dismissAgentError} />
          </AmberBanner>
        )}

        {agents.length === 0 ? (
          <ContentCard className="bg-white/[0.02] py-14 text-center">
            <p className="text-[14px] text-zinc-500">No agents yet</p>
            <p className="mt-1 text-[13px] text-zinc-600">
              Start a conversation to create an agent
            </p>
          </ContentCard>
        ) : (
          <AgentTable
            agents={agents}
            activeChatId={chat.activeChatId}
            actions={actions}
            onStop={handleStop}
            onDelete={handleDelete}
            onRename={setRenamingAgent}
          />
        )}
      </div>

      {renamingAgent && (
        <RenameDialog
          value={renamingAgent.name}
          onSave={(newName) => actions.handleRenameAgent(renamingAgent.id, newName)}
          onClose={() => setRenamingAgent(null)}
        />
      )}

      {confirmDeleteId && (
        <ModalOverlay className="z-[60] bg-black/50" onBackdropClick={() => setConfirmDeleteId(null)} cardClassName="mx-4">
          <h3 className="text-[15px] font-medium text-zinc-100">Delete Agent</h3>
          <p className="mt-2 text-[13px] leading-relaxed text-zinc-500">
            Are you sure you want to delete this agent? This will stop the runtime and remove all data. This action cannot be undone.
          </p>
          <div className="mt-5 flex justify-end gap-2">
            <ConfirmCancelButton onClick={() => setConfirmDeleteId(null)} />
            <ConfirmDeleteButton onClick={() => confirmDelete(confirmDeleteId)} />
          </div>
        </ModalOverlay>
      )}
    </FullPageShell>
  );
};
